import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

export type DeferredInlineEditUpdate<T> = {
  record: T;
  changes: Partial<T>;
};

type PendingRowChange<T> = DeferredInlineEditUpdate<T>;

export type MarkChangeOptions<T> = {
  clearFields?: (keyof T)[];
};

type DeferredInlineEditContextValue<T> = {
  enabled: boolean;
  hasPendingChanges: boolean;
  markChange: (
    rowKey: string,
    record: T,
    dataIndex: keyof T,
    value: T[keyof T],
    options?: MarkChangeOptions<T>,
  ) => void;
  clearRow: (rowKey: string) => void;
  getRowChanges: (rowKey: string) => Partial<T> | undefined;
  peekPendingUpdates: () => DeferredInlineEditUpdate<T>[];
  clearSubmittedUpdates: (
    updates: DeferredInlineEditUpdate<T>[],
    rowKeyName: keyof T,
  ) => void;
  takePendingUpdates: () => DeferredInlineEditUpdate<T>[];
};

const DeferredInlineEditContext =
  createContext<DeferredInlineEditContextValue<any> | null>(null);

export const useDeferredInlineEdit = <T,>() =>
  useContext(
    DeferredInlineEditContext,
  ) as DeferredInlineEditContextValue<T> | null;

type ProviderProps<T> = {
  enabled: boolean;
  onPendingChange?: (rowKey: string, record: T, changes: Partial<T>) => void;
  children: React.ReactNode;
};

export function DeferredInlineEditProvider<T>({
  enabled,
  onPendingChange,
  children,
}: ProviderProps<T>) {
  const onPendingChangeRef = useRef(onPendingChange);
  onPendingChangeRef.current = onPendingChange;

  const [pendingChanges, setPendingChanges] = useState<
    Map<string, PendingRowChange<T>>
  >(new Map());
  const pendingChangesRef = useRef(pendingChanges);
  pendingChangesRef.current = pendingChanges;

  const markChange = useCallback(
    (
      rowKey: string,
      record: T,
      dataIndex: keyof T,
      value: T[keyof T],
      options?: MarkChangeOptions<T>,
    ) => {
      setPendingChanges((prev) => {
        const next = new Map(prev);
        const current = next.get(rowKey);
        const existing: PendingRowChange<T> = current
          ? { record, changes: { ...current.changes } }
          : { record, changes: {} as Partial<T> };
        existing.record = record;

        const applyFieldValue = (field: keyof T, nextValue: T[keyof T]) => {
          const originalValue = record[field];
          const previousPending = existing.changes[field];
          const wasCleared =
            previousPending === null || previousPending === ('' as T[keyof T]);

          if (Object.is(nextValue, originalValue) && !wasCleared) {
            const changes = { ...existing.changes };
            delete changes[field];
            existing.changes = changes;
            return;
          }

          existing.changes = { ...existing.changes, [field]: nextValue };
        };

        applyFieldValue(dataIndex, value);

        options?.clearFields?.forEach((field) => {
          if (field === dataIndex) return;
          applyFieldValue(field, null as T[keyof T]);
        });

        if (Object.keys(existing.changes).length === 0) {
          next.delete(rowKey);
        } else {
          next.set(rowKey, existing);
        }

        const changes = existing.changes;
        queueMicrotask(() =>
          onPendingChangeRef.current?.(rowKey, record, changes),
        );
        return next;
      });
    },
    [],
  );

  const clearRow = useCallback((rowKey: string) => {
    setPendingChanges((prev) => {
      if (!prev.has(rowKey)) return prev;
      const next = new Map(prev);
      next.delete(rowKey);
      return next;
    });
  }, []);

  const getRowChanges = useCallback(
    (rowKey: string) => pendingChanges.get(rowKey)?.changes,
    [pendingChanges],
  );

  const peekPendingUpdates = useCallback(() => {
    return Array.from(pendingChangesRef.current.values())
      .filter(({ changes }) => Object.keys(changes).length > 0)
      .map(({ record, changes }) => ({
        record,
        changes: { ...changes },
      }));
  }, []);

  const clearSubmittedUpdates = useCallback(
    (updates: DeferredInlineEditUpdate<T>[], rowKeyName: keyof T) => {
      setPendingChanges((prev) => {
        const next = new Map(prev);
        let changed = false;

        updates.forEach(({ record, changes }) => {
          const key = String(record[rowKeyName]);
          const current = next.get(key);
          if (!current) return;

          const remaining = { ...current.changes };
          (Object.keys(changes) as Array<keyof T>).forEach((field) => {
            if (Object.is(remaining[field], changes[field])) {
              delete remaining[field];
            }
          });

          changed = true;
          if (Object.keys(remaining).length === 0) {
            next.delete(key);
          } else {
            next.set(key, { record: current.record, changes: remaining });
          }
        });

        return changed ? next : prev;
      });
    },
    [],
  );

  const takePendingUpdates = useCallback(() => {
    const rows = peekPendingUpdates();
    setPendingChanges(new Map());
    return rows;
  }, [peekPendingUpdates]);

  const value = useMemo<DeferredInlineEditContextValue<T>>(
    () => ({
      enabled,
      hasPendingChanges: pendingChanges.size > 0,
      markChange,
      clearRow,
      getRowChanges,
      peekPendingUpdates,
      clearSubmittedUpdates,
      takePendingUpdates,
    }),
    [
      clearRow,
      clearSubmittedUpdates,
      enabled,
      getRowChanges,
      markChange,
      pendingChanges,
      peekPendingUpdates,
      takePendingUpdates,
    ],
  );

  return (
    <DeferredInlineEditContext.Provider value={value}>
      {children}
    </DeferredInlineEditContext.Provider>
  );
}
